/**
 * Access key formatting — mirrors backend/app/core/access_key.py::normalize.
 *
 * Input spec: Crockford base32, 12 characters, I/L/O/U excluded.
 * Display spec: `XK4P-9M2H-RT7L` (groups of 4, uppercase, dashes).
 */

const CROCKFORD = /^[0-9A-HJKMNP-TV-Z]+$/

/**
 * Filter a raw input (paste / keystroke) down to the Crockford alphabet,
 * uppercased, limited to 12 chars. Whitespace and dashes are stripped.
 * Forbidden letters (I, L, O, U) are dropped silently.
 *
 * Returns the normalized 0..12 char string AND a flag indicating whether
 * any forbidden characters were filtered out. The UI shows a hint in that
 * case.
 */
export function sanitizeAccessKeyInput(raw: string): { value: string; filteredForbidden: boolean } {
  const upper = raw.toUpperCase()
  let value = ''
  let filteredForbidden = false
  for (const ch of upper) {
    if (ch === ' ' || ch === '-' || ch === '\t' || ch === '\n') continue
    if (ch === 'I' || ch === 'L' || ch === 'O' || ch === 'U') {
      filteredForbidden = true
      continue
    }
    if (CROCKFORD.test(ch)) {
      value += ch
      if (value.length === 12) break
    }
    // Any other char (punctuation, emoji, cyrillic) — silently dropped.
  }
  return { value, filteredForbidden }
}

/** Insert a dash every 4 chars: `XK4P9M2HRT7L` → `XK4P-9M2H-RT7L`. */
export function formatAccessKey(normalized: string): string {
  const parts: string[] = []
  for (let i = 0; i < normalized.length; i += 4) {
    parts.push(normalized.slice(i, i + 4))
  }
  return parts.join('-')
}

/** True when the normalized value is a full 12-char valid key. */
export function isCompleteAccessKey(normalized: string): boolean {
  return normalized.length === 12 && CROCKFORD.test(normalized)
}
